import axios from "axios";

/**
 * SearchService
 * Serviço para gerenciar requisições de pesquisas
 * @version 1.0.0
 */

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

const SearchService = {
  // ========== BUSCA BÁSICA ==========

  /**
   * Realizar busca global em todos os tipos de conteúdo
   * @param {string} query - Termo de busca
   * @param {number} limit - Número máximo de resultados
   * @param {number} skip - Número de resultados a pular
   * @returns {Promise<Object>} Resultados consolidados
   */
  globalSearch: async (query, limit = 10, skip = 0) => {
    try {
      const response = await axios.get(`${API_URL}/search/global`, {
        params: { q: query, limit, skip },
      });
      return response.data;
    } catch (error) {
      console.error("Error in globalSearch:", error);
      throw error;
    }
  },

  /**
   * Buscar em uma categoria específica
   * @param {string} type - Tipo de conteúdo (exercise, user, blog, lookbook)
   * @param {string} query - Termo de busca
   * @param {number} limit - Número máximo de resultados
   * @param {number} skip - Número de resultados a pular
   * @returns {Promise<Object>} Resultados da categoria
   */
  searchByCategory: async (type, query, limit = 10, skip = 0) => {
    try {
      const response = await axios.get(`${API_URL}/search/category/${type}`, {
        params: { q: query, limit, skip },
      });
      return response.data;
    } catch (error) {
      console.error("Error in searchByCategory:", error);
      throw error;
    }
  },

  /**
   * Obter sugestões de pesquisa (auto-complete)
   * @param {string} query - Termo parcial
   * @param {number} limit - Número máximo de sugestões
   * @returns {Promise<Array>} Array de sugestões
   */
  getSearchSuggestions: async (query, limit = 5) => {
    try {
      const response = await axios.get(`${API_URL}/search/suggestions`, {
        params: { q: query, limit },
      });
      return response?.data?.data?.suggestions || [];
    } catch (error) {
      console.error("Error in getSearchSuggestions:", error);
      return [];
    }
  },

  // ========== BUSCA AVANÇADA ==========

  /**
   * Realizar busca avançada com filtros
   * @param {string} query - Termo de busca
   * @param {Object} filters - Filtros adicionais
   * @param {Array} types - Tipos para buscar
   * @returns {Promise<Object>} Resultados filtrados
   */
  advancedSearch: async (query, filters = {}, types = ["exercise", "user"]) => {
    try {
      const response = await axios.post(`${API_URL}/search/advanced`, {
        query,
        filters,
        types,
      });
      return response.data;
    } catch (error) {
      console.error("Error in advancedSearch:", error);
      throw error;
    }
  },

  /**
   * Buscar pesquisas em um período de datas
   * @param {string} from - Data inicial (YYYY-MM-DD)
   * @param {string} to - Data final (YYYY-MM-DD)
   * @param {number} limit - Número máximo de resultados
   * @returns {Promise<Object>} Pesquisas no período
   */
  searchByDateRange: async (from, to, limit = 10) => {
    try {
      const response = await axios.get(`${API_URL}/search/by-date`, {
        params: { from, to, limit },
      });
      return response.data;
    } catch (error) {
      console.error("Error in searchByDateRange:", error);
      throw error;
    }
  },

  // ========== HISTÓRICO ==========

  /**
   * Obter histórico de pesquisas do usuário
   * @param {number} limit - Número máximo de itens
   * @returns {Promise<Array>} Histórico de pesquisas
   */
  getSearchHistory: async (limit = 10) => {
    try {
      const response = await axios.get(`${API_URL}/search/history`, {
        params: { limit },
      });
      return response?.data?.data?.history || [];
    } catch (error) {
      console.error("Error in getSearchHistory:", error);
      return [];
    }
  },

  /**
   * Limpar todo o histórico de pesquisas
   * @param {boolean} keepSaved - Manter pesquisas salvas
   * @returns {Promise<Object>} Resultado da limpeza
   */
  clearSearchHistory: async (keepSaved = true) => {
    try {
      const response = await axios.delete(`${API_URL}/search/history/clear`, {
        params: { keepSaved },
      });
      return response.data;
    } catch (error) {
      console.error("Error in clearSearchHistory:", error);
      throw error;
    }
  },

  // ========== PESQUISAS SALVAS ==========

  /**
   * Salvar uma pesquisa
   * @param {string} query - Termo de pesquisa
   * @param {string} savedName - Nome para salvar
   * @param {string} savedDescription - Descrição da pesquisa
   * @param {Object} filters - Filtros aplicados
   * @param {Array} resultTypes - Tipos de resultados
   * @returns {Promise<Object>} Pesquisa salva
   */
  saveSearch: async (
    query,
    savedName,
    savedDescription = "",
    filters = {},
    resultTypes = []
  ) => {
    try {
      const response = await axios.post(`${API_URL}/search/save`, {
        query,
        savedName,
        savedDescription,
        filters,
        resultTypes,
      });
      return response?.data?.data || {};
    } catch (error) {
      console.error("Error in saveSearch:", error);
      throw error;
    }
  },

  /**
   * Obter pesquisas salvas do usuário
   * @returns {Promise<Array>} Pesquisas salvas
   */
  getSavedSearches: async () => {
    try {
      const response = await axios.get(`${API_URL}/search/saved`);
      return response?.data?.data?.searches || [];
    } catch (error) {
      console.error("Error in getSavedSearches:", error);
      return [];
    }
  },

  /**
   * Deletar uma pesquisa salva
   * @param {string} searchId - ID da pesquisa
   * @returns {Promise<Object>} Resultado da exclusão
   */
  deleteSearch: async (searchId) => {
    try {
      const response = await axios.delete(`${API_URL}/search/${searchId}`);
      return response.data;
    } catch (error) {
      console.error("Error in deleteSearch:", error);
      throw error;
    }
  },

  // ========== COMPARTILHAMENTO ==========

  /**
   * Compartilhar uma pesquisa com outros usuários
   * @param {string} searchId - ID da pesquisa
   * @param {Array} userIds - IDs dos usuários
   * @returns {Promise<Object>} Pesquisa compartilhada
   */
  shareSearch: async (searchId, userIds) => {
    try {
      const response = await axios.post(`${API_URL}/search/${searchId}/share`, {
        userIds,
      });
      return response?.data?.data || {};
    } catch (error) {
      console.error("Error in shareSearch:", error);
      throw error;
    }
  },

  /**
   * Obter pesquisas compartilhadas com o usuário
   * @returns {Promise<Array>} Pesquisas compartilhadas
   */
  getSharedSearches: async () => {
    try {
      const response = await axios.get(`${API_URL}/search/shared-with-me`);
      return response?.data?.data?.searches || [];
    } catch (error) {
      console.error("Error in getSharedSearches:", error);
      return [];
    }
  },

  // ========== ANALYTICS E TRENDING ==========

  /**
   * Obter pesquisas trending
   * @param {number} limit - Número máximo de pesquisas
   * @returns {Promise<Array>} Pesquisas em trending
   */
  getTrendingSearches: async (limit = 10) => {
    try {
      const response = await axios.get(`${API_URL}/search/trending`, {
        params: { limit },
      });
      return response?.data?.data?.trending || [];
    } catch (error) {
      console.error("Error in getTrendingSearches:", error);
      return [];
    }
  },

  /**
   * Obter análise de uma pesquisa
   * @param {string} searchId - ID da pesquisa
   * @returns {Promise<Object>} Análise completa
   */
  getSearchAnalytics: async (searchId) => {
    try {
      const response = await axios.get(`${API_URL}/search/${searchId}/analytics`);
      return response?.data?.data || {};
    } catch (error) {
      console.error("Error in getSearchAnalytics:", error);
      return {};
    }
  },

  /**
   * Registrar interação com resultado de busca
   * @param {string} searchId - ID da pesquisa
   * @param {string} action - Tipo: click, impression, share, like
   * @returns {Promise<Object>} Dados atualizados
   */
  trackSearchInteraction: async (searchId, action = "click") => {
    try {
      const response = await axios.post(
        `${API_URL}/search/${searchId}/track`,
        { action }
      );
      return response?.data?.data || {};
    } catch (error) {
      console.error("Error in trackSearchInteraction:", error);
      return {};
    }
  },

  /**
   * Enviar feedback sobre uma pesquisa
   * @param {string} searchId - ID da pesquisa
   * @param {boolean} isHelpful - Foi útil?
   * @param {number} rating - Avaliação (1-5)
   * @param {string} feedback - Comentário
   * @returns {Promise<Object>} Feedback registrado
   */
  submitSearchFeedback: async (
    searchId,
    isHelpful = null,
    rating = null,
    feedback = ""
  ) => {
    try {
      const response = await axios.post(
        `${API_URL}/search/${searchId}/feedback`,
        { isHelpful, rating, feedback }
      );
      return response?.data?.data || {};
    } catch (error) {
      console.error("Error in submitSearchFeedback:", error);
      return {};
    }
  },

  // ========== ESTATÍSTICAS DO USUÁRIO ==========

  /**
   * Obter estatísticas de pesquisas do usuário
   * @returns {Promise<Object>} Estatísticas completas
   */
  getUserSearchStats: async () => {
    try {
      const response = await axios.get(`${API_URL}/search/user/stats`);
      return response?.data?.data || {};
    } catch (error) {
      console.error("Error in getUserSearchStats:", error);
      return {};
    }
  },

  // ========== UTILITÁRIOS ==========

  /**
   * Realizar busca com debounce para economizar requisições
   * @param {string} query - Termo de busca
   * @param {number} delayMs - Delay em milissegundos
   * @returns {Promise<Object>} Resultados
   */
  debouncedSearch: (() => {
    let timeoutId;
    return async (query, delayMs = 500) => {
      return new Promise((resolve, reject) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          SearchService.globalSearch(query)
            .then(resolve)
            .catch(reject);
        }, delayMs);
      });
    };
  })(),

  /**
   * Formatador de resultados para exibição
   * @param {Object} results - Resultados brutos
   * @returns {Object} Resultados formatados
   */
  formatResults: (results) => {
    if (!results || !results.data) return null;

    return {
      total: results.data.totalResults,
      exercises: results.data.results?.exercises?.items || [],
      users: results.data.results?.users?.items || [],
      blogs: results.data.results?.blogs?.items || [],
      lookbooks: results.data.results?.lookbooks?.items || [],
      time: results.data.executionTime,
    };
  },

  /**
   * Obter histórico formatado com timestamps legíveis
   * @param {Array} history - Histórico bruto
   * @returns {Array} Histórico formatado
   */
  formatHistory: (history) => {
    return history.map((item) => ({
      ...item,
      createdAtFormatted: new Date(item.createdAt).toLocaleDateString("pt-BR"),
      timeAgo: SearchService.getTimeAgo(new Date(item.createdAt)),
    }));
  },

  /**
   * Utilitário para calcular "tempo atrás"
   * @param {Date} date - Data
   * @returns {string} Descrição de tempo
   */
  getTimeAgo: (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) return "há poucos segundos";
    if (diffMins < 60) return `há ${diffMins} minuto${diffMins > 1 ? "s" : ""}`;
    if (diffHours < 24) return `há ${diffHours} hora${diffHours > 1 ? "s" : ""}`;
    if (diffDays < 30) return `há ${diffDays} dia${diffDays > 1 ? "s" : ""}`;
    return date.toLocaleDateString("pt-BR");
  },

  // ========== FUNCIONALIDADES ADICIONAIS ==========

  /**
   * Exportar histórico em formato JSON
   * @param {Array} history - Histórico de pesquisas
   * @returns {string} JSON stringificado
   */
  exportHistoryAsJSON: (history) => {
    return JSON.stringify(
      {
        exportedAt: new Date().toISOString(),
        totalSearches: history.length,
        searches: history,
      },
      null,
      2
    );
  },

  /**
   * Exportar histórico em formato CSV
   * @param {Array} history - Histórico de pesquisas
   * @returns {string} CSV formatado
   */
  exportHistoryAsCSV: (history) => {
    const headers = ["Query", "Tipo", "Data", "Resultados"];
    const rows = history.map((search) => [
      `"${search.query}"`,
      search.resultTypes?.join("|") || "multiple",
      new Date(search.createdAt).toLocaleString("pt-BR"),
      search.results?.total || 0,
    ]);

    return [headers, ...rows].map((row) => row.join(",")).join("\n");
  },

  /**
   * Fazer download de dados exportados
   * @param {string} content - Conteúdo a ser baixado
   * @param {string} filename - Nome do arquivo
   * @param {string} type - Tipo de conteúdo (application/json, text/csv)
   */
  downloadFile: (content, filename, type = "application/json") => {
    const element = document.createElement("a");
    element.setAttribute("href", `data:${type};charset=utf-8,${encodeURIComponent(content)}`);
    element.setAttribute("download", filename);
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    if (element.parentNode) {
      document.body.removeChild(element);
    }
  },

  /**
   * Obter preferências de pesquisa do localStorage
   * @returns {Object} Preferências salvas
   */
  getSearchPreferences: () => {
    const preferences = localStorage.getItem("searchPreferences");
    return preferences
      ? JSON.parse(preferences)
      : {
          autoSave: true,
          suggestionsEnabled: true,
          saveHistory: true,
          historyLimit: 50,
          darkMode: false,
        };
  },

  /**
   * Salvar preferências de pesquisa
   * @param {Object} preferences - Preferências a serem salvas
   */
  saveSearchPreferences: (preferences) => {
    localStorage.setItem("searchPreferences", JSON.stringify(preferences));
  },

  /**
   * Sincronizar pesquisas offline
   * @param {Array} offlineSearches - Pesquisas realizadas offline
   * @returns {Promise<Object>} Resultado da sincronização
   */
  syncOfflineSearches: async (offlineSearches) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/search/sync-offline`,
        { searches: offlineSearches },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response?.data || {};
    } catch (error) {
      console.error("Error syncing offline searches:", error);
      return {};
    }
  },

  /**
   * Obter sugestões com base em pesquisas recentes
   * @param {Array} history - Histórico de pesquisas
   * @returns {Array} Termos sugeridos
   */
  getSmartSuggestions: (history) => {
    if (!history || history.length === 0) return [];

    // Criar mapa de frequência
    const frequencyMap = {};
    history.forEach((search) => {
      const words = search.query.toLowerCase().split(" ");
      words.forEach((word) => {
        if (word.length > 2) {
          frequencyMap[word] = (frequencyMap[word] || 0) + 1;
        }
      });
    });

    // Ordenar por frequência
    return Object.entries(frequencyMap)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([word]) => word);
  },

  /**
   * Validar termo de busca
   * @param {string} query - Termo a validar
   * @returns {Object} Resultado da validação
   */
  validateSearchQuery: (query) => {
    const errors = [];
    const warnings = [];

    if (!query) {
      errors.push("O termo de busca não pode estar vazio");
    }

    if (query.length < 2) {
      errors.push("O termo de busca deve ter no mínimo 2 caracteres");
    }

    if (query.length > 500) {
      errors.push("O termo de busca não pode exceder 500 caracteres");
    }

    if (query.length > 100) {
      warnings.push("Termo de busca muito longo, considere refinar");
    }

    // Detectar SQL injection simples
    const sqlPatterns = /(\b(UNION|SELECT|INSERT|DELETE|UPDATE|DROP)\b)/gi;
    if (sqlPatterns.test(query)) {
      warnings.push("Sua pesquisa contém caracteres especiais");
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
      length: query.length,
    };
  },

  /**
   * Formatar termo de busca para melhor resultado
   * @param {string} query - Termo a formatar
   * @returns {string} Termo formatado
   */
  normalizeSearchQuery: (query) => {
    return query
      .trim() // Remove espaços em branco
      .replace(/\s+/g, " ") // Remove espaços múltiplos
      .toLowerCase() // Converte para minúsculas
      .normalize("NFD") // Remove acentos
      .replace(/[\u0300-\u036f]/g, "");
  },

  /**
   * Combinar múltiplas buscas (AND, OR)
   * @param {string} term1 - Primeiro termo
   * @param {string} term2 - Segundo termo
   * @param {string} operator - Operador (AND, OR)
   * @returns {string} Query combinada
   */
  combineSearchTerms: (term1, term2, operator = "AND") => {
    return `${term1} ${operator.toUpperCase()} ${term2}`;
  },

  /**
   * Análise de resultados de pesquisa
   * @param {Object} results - Resultados da pesquisa
   * @returns {Object} Análise detalhada
   */
  analyzeSearchResults: (results) => {
    if (!results) {
      return {
        totalResults: 0,
        byType: {},
        isEmpty: true,
        executionTime: 0,
      };
    }

    const byType = {};
    let totalResults = 0;

    Object.keys(results).forEach((key) => {
      if (Array.isArray(results[key])) {
        const count = results[key].length;
        byType[key] = count;
        totalResults += count;
      }
    });

    return {
      totalResults,
      byType,
      isEmpty: totalResults === 0,
      types: Object.keys(byType),
      mostCommonType: Object.entries(byType).sort(([, a], [, b]) => b - a)[0]?.[0],
      percentageByType: Object.entries(byType).reduce(
        (acc, [key, count]) => ({
          ...acc,
          [key]: `${((count / totalResults) * 100).toFixed(1)}%`,
        }),
        {}
      ),
    };
  },
};

export default SearchService;
